/**
 * `TravelerLookupResult` — the anti-enumeration response shape of the identity lookups
 * `/internal/lookup/{phone,email,id,public-id}` (LK-01 / LK-02). Their `/access` variants
 * answer `TravelerAccessLookupResult`: the same traveler plus the policy-api reads.
 *
 * DESIGN DECISION (locked): uniform `200 + { found, traveler | null }`. Found
 * and not-found return the IDENTICAL top-level shape AND status code, so an
 * in-VPC caller cannot infer phone/email existence from the response
 * structure or the HTTP status (Information Disclosure / enumeration — the
 * service has NO app-layer auth under D-US-06, so any in-VPC caller can
 * probe). The consumer branches on `found`, never on the status code.
 *
 * The nested `traveler` is a MINIMAL projection — only what `multichannel-auth`
 * needs plus `vollUserId` (= the service-local domain `User.id`, brand-free
 * here). It is deliberately NOT the full `TravelerView`: the lookup leaks less
 * by returning less (no `createdAt`, no extra attrs). `found: false` carries
 * `traveler: null`.
 */
import { z } from 'zod';

import { NotificationChannelSchema } from '../customer_api/notification_channel.js';
import { ApprovalDefaultsSchema, PolicyAccessSchema } from '../policy/policy_access.js';

export const PolicyAccessStatusSchema = z.enum(['resolved', 'unavailable']);
export type PolicyAccessStatus = z.infer<typeof PolicyAccessStatusSchema>;

export const TravelerLookupSchema = z.object({
  // Opaque, stable per-customer key (D-PID-02) — the deterministic UUIDv5 minted
  // from the legacy source id. Prefer over `vollUserId` (raw, PII-adjacent) for
  // downstream attribution (e.g. Langfuse trace userId).
  publicId: z.string(),
  vollUserId: z.string(),
  name: z.string().nullable(),
  email: z.email().nullable(),
  phone: z.string().nullable(),
  companyId: z.string().nullable(),
  // The company's corporate group (`business.clients.corporateGroup.id`): the key a
  // Slack workspace is linked by. `.optional()` so older fixtures keep parsing; the
  // customer-api lookup always sends it (null when the company has no group).
  corporateGroupId: z.string().nullable().optional(),
  // L1 enrichment (customer data joined at lookup time): company display name
  // (`business.clients`) + VIP status/level (`business.usersVip`). `vip` is the
  // presence of a usersVip record; `vipLevel` its `level` (e.g. "PRIME") or null.
  companyName: z.string().nullable(),
  vip: z.boolean(),
  vipLevel: z.string().nullable(),
  // L1 enrichment (cont.): the company's default conversation language
  // (`business.clients.language`, mirrored onto the customer `Company`). BCP-47
  // tag (e.g. `pt-BR`). Nullable — the source may omit it; downstream seeds the
  // conversation language + agent locale from it when present.
  language: z.string().nullable(),
  // L1 enrichment (cont.): rateio default (`business.user_costcenter`). Fail-soft to
  // `null` — a missing source never turns a found traveler into not-found.
  approvalDefaults: ApprovalDefaultsSchema.nullable(),
  // Proactive delivery channel preference (D-PC-04). Lazy default 'email' when no
  // stored record — never null.
  preferredChannel: NotificationChannelSchema,
  // Numeric profile/role enum (`business.users.profileId`, OWNER=0…REQUESTER=6),
  // mirrored onto the customer. Consumed by the approval round-trip as the
  // outbound `userProfile` of an approval decision. `.optional()` (not just
  // `.nullable()`) so pre-existing consumers/fixtures that never sent the field
  // keep parsing; the customer-api lookup always populates it (null when the
  // source omits it).
  profileId: z.number().nullable().optional(),
  // `false` when the corporate registry deactivated the traveler (#6938): the BFF refuses
  // login, refresh and gated routes with `user_inactive`. `.optional()` so older fixtures
  // keep parsing; absent reads as active. The customer-api lookup always sends it.
  active: z.boolean().optional(),
  // The traveler's OWN corporate group (`business.users.corporateGroupId`), which the legacy
  // app token carries: the BFF ends a session whose token group no longer matches it (#6995).
  // Not `corporateGroupId` above (the company's group). Optional for older fixtures.
  userCorporateGroupId: z.string().nullable().optional(),
});
export type TravelerLookup = z.infer<typeof TravelerLookupSchema>;

export const TravelerLookupResultSchema = z.object({
  found: z.boolean(),
  traveler: TravelerLookupSchema.nullable(),
});
export type TravelerLookupResult = z.infer<typeof TravelerLookupResultSchema>;

/**
 * The `/internal/lookup/{kind}/access` traveler: the identity plus the policy-api reads
 * (`policyGetAccessModal`). Only this lookup carries them; the identity lookup never
 * calls the policy-api and sends none of these keys (#3512). Every read is fail-soft to
 * `null` — a failed policy call never turns a found traveler into not-found.
 */
export const TravelerAccessLookupSchema = TravelerLookupSchema.extend({
  policyAccess: PolicyAccessSchema.nullable(),
  // Whether `policyAccess` is an ANSWER or a gap (#5232): `unavailable` = the policy-api
  // read failed (timeout/network/non-200), so the `null` beside it means "could not
  // tell", not "no product". `resolved` covers every other case, including the
  // deterministic `null`s (no company, no profile). A gate stays closed either way; it
  // only answers "try again" instead of "not enabled".
  policyAccessStatus: PolicyAccessStatusSchema,
  // `policyGetAccessModal.seat` — the post-issuance seat marking (the ancillary seat
  // gate), read in a call of its OWN and kept OUT of `policyAccess`: that list stays
  // the closed product enum the deployed consumers parse. `null` = unresolved (policy
  // not wired, no company/profile, call failed).
  seatAccess: z.boolean().nullable(),
});
export type TravelerAccessLookup = z.infer<typeof TravelerAccessLookupSchema>;

export const TravelerAccessLookupResultSchema = z.object({
  found: z.boolean(),
  traveler: TravelerAccessLookupSchema.nullable(),
});
export type TravelerAccessLookupResult = z.infer<typeof TravelerAccessLookupResultSchema>;
